/**
 * Assistant Telemetry Sink
 *
 * `createPluginChatAdapter` already emits telemetry by default (console +
 * a private in-memory event bus, since the overlay doesn't pass a real
 * `events` bridge) — see `buildTelemetry` in
 * @neuro/chat-adapter/plugin-helper.ts. That's invisible to the platform's
 * own analytics though. This sink forwards the meaningful chat-adapter
 * telemetry events into the host's existing `trackEvent` (utils/analytics.ts)
 * so assistant usage shows up alongside every other tracked event instead
 * of only in the browser console.
 *
 * `stream.delta` fires once per streaming chunk — far too high-volume to
 * be a meaningful analytics event — so it's deliberately not forwarded.
 */
import type { TelemetryEvent, TelemetrySink } from '@neuro/chat-adapter';
import { trackEvent } from '../../utils/analytics';

const ANALYTICS_PREFIX = 'assistant_overlay';

export function createAssistantAnalyticsSink(): TelemetrySink {
  return {
    emit(event: TelemetryEvent) {
      switch (event.type) {
        case 'turn.start':
          trackEvent(`${ANALYTICS_PREFIX}_turn_start`, {
            messageCount: event.messageCount,
            hasContext: event.hasContext,
            toolCount: event.toolCount,
          });
          break;
        case 'turn.end':
          trackEvent(`${ANALYTICS_PREFIX}_turn_end`, {
            durationMs: event.durationMs,
            totalTokens: event.totalTokens,
            toolCallCount: event.toolCallCount,
            model: event.model,
            finishReason: event.finishReason,
          });
          break;
        case 'turn.error':
          trackEvent(`${ANALYTICS_PREFIX}_turn_error`, {
            durationMs: event.durationMs,
            errorType: event.errorType,
            retryable: event.retryable,
          });
          break;
        case 'tool.start':
          trackEvent(`${ANALYTICS_PREFIX}_tool_start`, { toolName: event.toolName });
          break;
        case 'tool.end':
          trackEvent(`${ANALYTICS_PREFIX}_tool_end`, {
            toolName: event.toolName,
            durationMs: event.durationMs,
            success: event.success,
          });
          break;
        case 'knowledge.query':
          trackEvent(`${ANALYTICS_PREFIX}_knowledge_query`, {
            queryType: event.queryType,
            resultCount: event.resultCount,
            durationMs: event.durationMs,
          });
          break;
        case 'stream.delta':
          // Too high-volume to be a useful analytics event — skip.
          break;
      }
    },
  };
}
